import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {isThreadOutdated} from '../../../helpers/thread-navigation'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {CommentsFilterState} from '../use-comments-filter-state'
import type {ConversationComment} from '../../../page-data/payloads/conversation-comments'
import {isBotOrAgentCommentAuthor} from '../../../page-data/payloads/comment-interfaces'

/**
 * Data on each item that is used to determine if it matches the current filter
 */
interface ItemFilterData {
  authorLogin: string
  isBotOrAgent: boolean
  isOutdated: boolean
  isResolved: boolean
  isHidden: boolean
}

function getThreadFilterData(thread: Thread): ItemFilterData {
  const firstComment = getFirstComment(thread)

  return {
    authorLogin: firstComment?.author?.login ?? '',
    isBotOrAgent: isBotOrAgentCommentAuthor(firstComment?.author),
    isResolved: thread.isResolved ?? false,
    isOutdated: isThreadOutdated(thread),
    isHidden: firstComment?.isHidden ?? false,
  }
}

function getConversationCommentFilterData(item: ConversationComment): ItemFilterData {
  switch (item.type) {
    case 'issue':
      return {
        authorLogin: item.author?.login ?? '',
        isBotOrAgent: isBotOrAgentCommentAuthor(item.author),
        isResolved: item.isHidden && item.minimizedReason === 'RESOLVED',
        isOutdated: item.isHidden && item.minimizedReason === 'OUTDATED',
        isHidden: item.isHidden,
      }
    case 'review_summary': {
      return {
        authorLogin: item.author?.login ?? '',
        isBotOrAgent: isBotOrAgentCommentAuthor(item.author),
        isResolved: item.isHidden && item.minimizedReason === 'RESOLVED',
        isOutdated: item.isHidden && item.minimizedReason === 'OUTDATED',
        isHidden: item.isHidden,
      }
    }
  }
}

export function isEmptyReview(item: ConversationComment) {
  return (
    item.type === 'review_summary' &&
    item.state === 'COMMENTED' &&
    item.body.trim().length === 0 &&
    item.reviewThreadIds.length === 0
  )
}

export function filterThread(thread: Thread, filterState: CommentsFilterState) {
  return filterItem(getThreadFilterData(thread), filterState)
}

/**
 * Return a set of thread ids that match the current filter state
 */
export function filterThreads(threadPreviews: readonly Thread[], filterState: CommentsFilterState) {
  return threadPreviews.filter(thread => filterThread(thread, filterState))
}

/** Remove comments we cannot show to the user at all. These comments are ignored in counts and other places. */
export function filterRenderableComments(items: readonly ConversationComment[]) {
  return items.filter(item => !isEmptyReview(item))
}

export function filterConversationComments(items: readonly ConversationComment[], filterState: CommentsFilterState) {
  return items.filter(item => {
    if (!filterItem(getConversationCommentFilterData(item), filterState)) return false
    return true
  })
}

function filterItem(data: ItemFilterData, filterState: CommentsFilterState) {
  const {hideResolvedThreads, hideOutdatedThreads, hideHiddenComments} = filterState

  if (hideResolvedThreads && data.isResolved) {
    return false
  }
  if (hideOutdatedThreads && data.isOutdated) {
    return false
  }
  if (hideHiddenComments && data.isHidden) {
    return false
  }

  if (filterState.showAgents === false && data.isBotOrAgent) {
    return false
  }

  if (filterState.selectedAuthors && !filterState.selectedAuthors.includes(data.authorLogin)) {
    return false
  }

  return true
}
